import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { Trash2, X, Pencil } from "lucide-react";

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / 86400000);
}

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [haulers, setHaulers] = useState([]);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    const [d, h] = await Promise.all([api.get("/drivers"), api.get("/haulers")]);
    setDrivers(d.data); setHaulers(h.data);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    if (!window.confirm("Remove this driver?")) return;
    await api.delete(`/drivers/${id}`);
    refresh();
  };
  const haulerName = (id) => haulers.find((h) => h.id === id)?.company_name || null;

  return (
    <div>
      <PageHeader
        title="Drivers"
        subtitle="CDL roster"
        actions={
          <button onClick={() => setEditing("new")} data-testid="new-driver-button" className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-2.5">
            + New Driver
          </button>
        }
      />
      <div className="p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="drivers-list">
          {drivers.length === 0 ? (
            <div className="col-span-full text-center text-sm text-gray-500 py-12 bg-white border border-gray-200">No drivers yet.</div>
          ) : drivers.map((d) => {
            const cdlDays = daysUntil(d.cdl_expiry);
            const cdlExpired = cdlDays !== null && cdlDays < 0;
            const cdlWarn = cdlDays !== null && cdlDays >= 0 && cdlDays <= 30;
            return (
              <div key={d.id} className="bg-white border border-gray-200 p-5" data-testid={`driver-card-${d.cdl_number}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gray-900 text-white flex items-center justify-center font-display font-black text-lg">
                    {d.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-gray-900 truncate">{d.name}</div>
                    <div className="font-mono text-xs text-gray-500">{d.cdl_number} {d.cdl_class && `· ${d.cdl_class}`}</div>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-3 text-xs space-y-1 text-gray-700">
                  <div>{d.phone || "—"}</div>
                  <div className="truncate">{d.email || "—"}</div>
                  {haulerName(d.hauler_id) && <div className="text-gray-500">{haulerName(d.hauler_id)}</div>}
                  {d.cdl_expiry && (
                    <div className={`font-mono ${cdlExpired ? "text-red-700 font-bold" : cdlWarn ? "text-amber-700" : "text-gray-500"}`}>
                      CDL exp: {d.cdl_expiry} {cdlExpired ? "· EXPIRED" : cdlWarn ? `· ${cdlDays}d` : ""}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-gray-700">{(d.status || "").replace("_", " ")}</span>
                  <div className="flex gap-1">
                    <button onClick={() => setEditing(d)} className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100" data-testid={`edit-driver-${d.cdl_number}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(d.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <DriverDialog
          initial={editing === "new" ? null : editing}
          haulers={haulers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function DriverDialog({ initial, haulers, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    cdl_number: initial?.cdl_number || "",
    cdl_class: initial?.cdl_class || "A",
    cdl_expiry: initial?.cdl_expiry || "",
    phone: initial?.phone || "",
    email: initial?.email || "",
    sms_email: initial?.sms_email || "",
    status: initial?.status || "available",
    hauler_id: initial?.hauler_id || "",
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, hauler_id: form.hauler_id || null, cdl_expiry: form.cdl_expiry || null };
      if (isEdit) await api.patch(`/drivers/${initial.id}`, payload);
      else await api.post("/drivers", payload);
      toast.success(isEdit ? "Driver updated" : "Driver added");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-md">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold text-xl">{isEdit ? "Edit Driver" : "New Driver"}</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <F label="Full Name"><input required value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" data-testid="driver-name-input" /></F>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><F label="CDL #"><input required value={form.cdl_number} onChange={(e) => set("cdl_number", e.target.value)} className="inp font-mono" data-testid="driver-cdl-input" /></F></div>
            <F label="Class">
              <select value={form.cdl_class} onChange={(e) => set("cdl_class", e.target.value)} className="inp" data-testid="driver-cdl-class-select">
                <option value="A">A</option><option value="B">B</option><option value="C">C</option>
              </select>
            </F>
          </div>
          <F label="CDL Expiry"><input type="date" value={form.cdl_expiry} onChange={(e) => set("cdl_expiry", e.target.value)} className="inp font-mono" data-testid="driver-cdl-expiry-input" /></F>
          <F label="Hauler">
            <select value={form.hauler_id} onChange={(e) => set("hauler_id", e.target.value)} className="inp" data-testid="driver-hauler-select">
              <option value="">Unassigned</option>
              {haulers.map((h) => <option key={h.id} value={h.id}>{h.company_name}</option>)}
            </select>
          </F>
          <F label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="inp" data-testid="driver-phone-input" /></F>
          <F label="Email"><input value={form.email} onChange={(e) => set("email", e.target.value)} className="inp" data-testid="driver-email-input" /></F>
          <F label="SMS-via-Email (optional)">
            <input value={form.sms_email} onChange={(e) => set("sms_email", e.target.value)} className="inp font-mono" data-testid="driver-sms-email-input" placeholder="5551234567@vtext.com" />
          </F>
          <F label="Status">
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className="inp" data-testid="driver-status-select">
              <option value="available">Available</option>
              <option value="on_route">On Route</option>
              <option value="off_duty">Off Duty</option>
            </select>
          </F>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button type="submit" disabled={busy} data-testid="submit-driver-button" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-xs uppercase tracking-widest font-semibold">{isEdit ? "Update" : "Save"}</button>
          </div>
        </form>
        <style>{`.inp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; } .inp:focus { outline: none; border-color: #111827; }`}</style>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="label-caps block mb-1.5">{label}</label>{children}</div>;
}
